/* eslint-disable */
/**
 * Ingredients Glossary - Vue 3 (CDN build) using Composition API.
 * Product-based implementation (NetSuite ERP sync compatible)
 * 
 * This file queries Shopify Products with product_type:"ingredient" instead of Metaobjects.
 * Uses Vue's CDN version and modern composition patterns.
 */

/**
 * @typedef {Object} ResolvedImage
 * @property {string} id - Unique ID for the image (e.g., "gid://shopify/ImageSource/28681348743305")
 * @property {string} url - PNG image URL from Shopify CDN
 * @property {string|null} altText - Alt text for the image (can be null)
 * @property {number} width - Original width of the image in pixels
 * @property {number} height - Original height of the image in pixels
 * @property {string|null} thumbhash - ThumbHash of the image for placeholder generation
 */

/**
 * @typedef {Object} Ingredient
 * @property {string} id - Product GID (e.g., "gid://shopify/Product/123456789")
 * @property {string} handle - URL-friendly handle (e.g., "aloe-vera")
 * @property {string} title - Display name of the ingredient (cleaned, "Ingredient - " prefix removed)
 * @property {string} originalTitle - Original product title from Shopify (preserved for debugging)
 * @property {string|null} type - Comma-separated type names (from netsuite.ingredients_types metafield)
 * @property {string|null} typeName - Resolved type name (first type from comma-separated list)
 * @property {string|null} description - HTML description (from product description)
 * @property {string|null} featuredImage - Direct image URL (not GID)
 * @property {string|null} featuredImageAlt - Alt text for featured image
 * @property {number|null} featuredImageWidth - Image width in pixels
 * @property {number|null} featuredImageHeight - Image height in pixels
 * @property {string|null} previewTitle - Preview title for card display (cleaned)
 * @property {string|null} previewIcon - Preview icon (not used in product implementation)
 * @property {string|null} previewDescription - Preview description for card display
 * @property {string|null} attributeInciName - INCI Name attribute
 * @property {string|null} attributeIngredientOrigins - Ingredient origins attribute
 * @property {string|null} attributeCommonName - Common name attribute
 * @property {string|null} attributeRoles - Comma-separated role names (ready to display)
 * @property {string|null} attributeEwgScore - EWG score (numeric as string)
 * @property {ResolvedImage|null} [resolvedImage] - Pre-populated image data (no lazy loading)
 * @property {string} [resolvedRoles] - Pre-populated role names (no lazy loading)
 */

const { createApp, ref } = Vue
const storefrontAccessToken = "4ef165d1b077ebb0afca1870abeccb5a"
const graphqlVersion = "2025-10"
const INITIAL_VISIBLE_GROUPS = 2
const DEFAULT_TYPE_COLOR = '#cccccc'

// Type color mapping from Liquid data attribute (dynamic from metaobjects)
let TYPE_COLOR_MAP = {}
try {
  const appElement = document.getElementById('IngredientsGlossary')
  const typeColorsData = appElement?.dataset.typeColors
  if (typeColorsData) {
    TYPE_COLOR_MAP = JSON.parse(typeColorsData)
  }
} catch (e) {
  console.warn('Could not parse type colors from data attribute, using defaults')
}

// GraphQL Queries
const QUERY_INGREDIENT_PRODUCTS = `
  query getIngredientProducts($first: Int!, $query: String!) {
    products(first: $first, query: $query, sortKey: TITLE, reverse: false) {
      edges {
        node {
          id
          handle
          title
          description
          images(first: 1) {
            edges {
              node {
                id
                url
                altText
                width
                height
              }
            }
          }
          metafields(
            identifiers: [
              { namespace: "netsuite", key: "ingredients_types" }
              { namespace: "netsuite", key: "ingredients_short_description" }
              { namespace: "netsuite", key: "ingredients_inci_name" }
              { namespace: "netsuite", key: "ingredients_origins" }
              { namespace: "netsuite", key: "ingredients_common_name" }
              { namespace: "netsuite", key: "ingredients_role" }
              { namespace: "netsuite", key: "ingredients_ewg_score" }
              { namespace: "netsuite", key: "ingredients_main_image_url" }
            ]
          ) {
            key
            value
            namespace
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

// QUERY_RESOLVE_IMAGES removed - product images are direct URLs
// QUERY_RESOLVE_ROLES removed - roles are comma-separated strings

createApp({
  delimiters: ['[[', ']]'],
  setup() {
    const ingredients = ref([])
    const ingredientsByLetter = ref([])
    const selectedTypes = ref([])
    
    // Pagination state
    const hasNextPage = ref(false)
    const endCursor = ref(null)
    const isLoading = ref(true) // Start with loading state
    const fetchError = ref(false) // Error state
    
    // View state
    const currentView = ref(1) // 1 = sequential, 2 = letter overlay
    const view1ScrollPosition = ref(0)
    const currentOverlayLetter = ref(null)
    const visibleLetterGroupCount = ref(INITIAL_VISIBLE_GROUPS)
    const emptyLetterGroups = ref([])
    
    // Mobile filter drawer state
    const isDrawerOpen = ref(false)
    
    // Ingredient detail drawer state
    const isIngredientDrawerOpen = ref(false)
    const selectedIngredient = ref(null)
    
    return {
      ingredients,
      ingredientsByLetter,
      selectedTypes,
      hasNextPage,
      endCursor,
      isLoading,
      fetchError,
      currentView,
      view1ScrollPosition,
      currentOverlayLetter,
      visibleLetterGroupCount,
      emptyLetterGroups,
      isDrawerOpen,
      isIngredientDrawerOpen,
      selectedIngredient,
    }
  },
  computed: {
    // Merge data groups with empty groups, sorted alphabetically
    displayGroups() {
      const alphabet = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      const allGroups = []
      
      // Add all data groups
      this.ingredientsByLetter.forEach(group => {
        allGroups.push({
          letter: group.letter,
          ingredients: group.ingredients,
          isEmpty: false
        })
      })
      
      // Add empty letter groups
      this.emptyLetterGroups.forEach(letter => {
        // Only add if not already in data groups
        const exists = allGroups.find(g => g.letter === letter)
        if (!exists) {
          allGroups.push({
            letter: letter,
            ingredients: [],
            isEmpty: true
          })
        }
      })
      
      // Sort alphabetically (# first, then A-Z)
      allGroups.sort((a, b) => {
        const indexA = alphabet.indexOf(a.letter)
        const indexB = alphabet.indexOf(b.letter)
        return indexA - indexB
      })
      
      return allGroups
    },
    
    // Only show first N letter groups
    visibleIngredientsByLetter() {
      return this.displayGroups.slice(0, this.visibleLetterGroupCount)
    },
    
    // Check if filtered results are empty
    hasNoFilteredResults() {
      return this.selectedTypes.length > 0 && this.ingredientsByLetter.length === 0
    }
  },
  methods: {
    // ============================================================================
    // Data Fetching Methods
    // ============================================================================
    
    async fetchIngredients() {
      try {
        const response = await fetch(`/api/${graphqlVersion}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': storefrontAccessToken
          },
          body: JSON.stringify({ 
            query: QUERY_INGREDIENT_PRODUCTS,
            variables: { first: 250, query: "product_type:Ingredient status:active" }
          })
        })
        
        const data = await response.json()
        
        if (data.errors) {
          console.error('❌ GraphQL errors:', data.errors)
          this.fetchError = true
          return
        }
        
        if (!data.data?.products) {
          console.error('❌ Unexpected response format')
          this.fetchError = true
          return
        }
        
        this.ingredients = this.organizeIngredientsData(data.data.products.edges)
        this.ingredientsByLetter = this.groupIngredientsByLetter(this.ingredients)
      } catch (error) {
        console.error('❌ Error fetching ingredients:', error)
        this.fetchError = true
      }
    },
    
    /**
     * Organize raw GraphQL product data into Ingredient objects
     * @param {Array} edges - Raw GraphQL edges from products query
     * @returns {Ingredient[]} Array of organized ingredient objects
     */
    organizeIngredientsData(edges) {
      let cleanedCount = 0 // Track how many titles were cleaned
      
      return edges.map(edge => {
        const product = edge.node
        
        // Helper to clean title - removes "Ingredient - " prefix from importer error
        const cleanTitle = (title) => {
          if (!title) return title
          const prefix = 'Ingredient - '
          if (title.startsWith(prefix)) {
            cleanedCount++
            const cleaned = title.slice(prefix.length).trim()
            // Ensure we don't end up with empty string
            return cleaned || title
          }
          return title
        }
        
        // Helper to get metafield value by key
        const getMetafield = (key) => {
          const metafield = product.metafields.find(m => 
            m && m.namespace === 'netsuite' && m.key === key
          )
          return metafield ? metafield.value : null
        }
        
        // Clean the title - this becomes the source of truth
        const cleanedTitle = cleanTitle(product.title)
        
        // Get first product image (or null)
        const firstImage = product.images.edges[0]?.node || null

        // Optional override: Netsuite main image URL metafield (raw URL)
        const mainImageUrl = getMetafield('ingredients_main_image_url')
        
        // Get short description with fallback to cropped main description
        const shortDesc = getMetafield('ingredients_short_description') || 
                          (product.description ? this.cropDescription(product.description, 150) : null)
        
        // Resolve image data, preferring Netsuite URL when available
        const resolvedImageUrl = mainImageUrl || (firstImage ? firstImage.url : null)
        
        /** @type {Ingredient} */
        const ingredient = {
          id: product.id,
          handle: product.handle,
          title: cleanedTitle, // Use cleaned title (removed "Ingredient - " prefix)
          originalTitle: product.title, // Preserve original for debugging
          type: getMetafield('ingredients_types'), // Comma-separated string
          typeName: null, // Will be resolved in Phase 2
          description: product.description, // Full HTML description for drawer
          featuredImage: resolvedImageUrl, // Prefer Netsuite URL, fallback to product image
          featuredImageAlt: firstImage ? firstImage.altText : null,
          featuredImageWidth: firstImage ? firstImage.width : null,
          featuredImageHeight: firstImage ? firstImage.height : null,
          previewTitle: cleanedTitle, // Use cleaned title for preview
          previewIcon: null, // Not used in glossary
          previewDescription: shortDesc, // Short desc for card
          attributeInciName: getMetafield('ingredients_inci_name'),
          attributeIngredientOrigins: getMetafield('ingredients_origins'),
          attributeCommonName: getMetafield('ingredients_common_name'),
          attributeRoles: getMetafield('ingredients_role'), // Comma-separated names (ready to display)
          attributeEwgScore: getMetafield('ingredients_ewg_score'),
          resolvedImage: resolvedImageUrl ? {
            id: firstImage ? firstImage.id : null,
            url: resolvedImageUrl,
            altText: firstImage ? firstImage.altText : product.title,
            width: firstImage ? firstImage.width : null,
            height: firstImage ? firstImage.height : null
          } : null, // Prefer Netsuite URL, fallback to product image (no resolution needed)
          resolvedRoles: getMetafield('ingredients_role') || '' // Direct role names (no resolution needed)
        }
        
        return ingredient
      }).map((ingredient, index, array) => {
        // Log cleanup summary after all ingredients processed (only on last iteration)
        if (index === array.length - 1 && cleanedCount > 0) {
          console.log(`✅ Title Cleanup: Removed "Ingredient - " prefix from ${cleanedCount} of ${array.length} ingredient titles`)
        }
        return ingredient
      })
    },
    
    /**
     * Crop HTML description to plain text with max length
     * @param {string} html - HTML description string
     * @param {number} maxLength - Maximum character length
     * @returns {string} Cropped plain text with ellipsis if needed
     */
    cropDescription(html, maxLength) {
      if (!html) return ''
      // Strip HTML tags
      const text = html.replace(/<[^>]*>/g, '')
      // Crop to maxLength
      return text.length > maxLength 
        ? text.substring(0, maxLength) + '...' 
        : text
    },
    
    /**
     * Group ingredients by their first letter
     * @param {Ingredient[]} ingredients - Array of ingredient objects
     * @returns {Array} Array of letter groups with ingredients
     */
    groupIngredientsByLetter(ingredients) {
      const grouped = {}
      
      // Group ingredients by first letter (no pre-initialization)
      ingredients.forEach(ingredient => {
        if (!ingredient.title) return
        
        const firstChar = ingredient.title.charAt(0).toUpperCase()
        const letter = /[A-Z]/.test(firstChar) ? firstChar : '#'
        
        if (!grouped[letter]) {
          grouped[letter] = []
        }
        grouped[letter].push(ingredient)
      })
      
      // Return ordered array with ONLY letters that have data
      // STRICT alphabetical order: # first, then A-Z (only populated ones)
      const orderedGroups = []
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
      
      // Add # group first if it has ingredients
      if (grouped['#'] && grouped['#'].length > 0) {
        orderedGroups.push({ letter: '#', ingredients: grouped['#'] })
      }
      
      // Add A-Z groups ONLY if they have ingredients, in strict alphabetical order
      alphabet.forEach(letter => {
        if (grouped[letter] && grouped[letter].length > 0) {
          orderedGroups.push({ letter, ingredients: grouped[letter] })
        }
      })
      
      return orderedGroups
    },
    
    
    // ============================================================================
    // Type Resolution & Lookup
    // ============================================================================
    
    resolveTypeName(typeString) {
      if (!typeString) return 'Unknown Type'
      // Parse comma-separated types and return first
      const types = typeString.split(',').map(t => t.trim())
      return types[0] || 'Unknown Type'
    },
    
    getTypeColor(typeString) {
      if (!typeString) return DEFAULT_TYPE_COLOR
      
      const typeName = this.resolveTypeName(typeString)
      return TYPE_COLOR_MAP[typeName] || DEFAULT_TYPE_COLOR
    },
    
    resolveIngredientTypes() {
      if (!this.ingredients || !Array.isArray(this.ingredients)) {
        return
      }
      this.ingredients.forEach(ingredient => {
        if (ingredient) {
          ingredient.typeName = this.resolveTypeName(ingredient.type)
        }
      })
      this.ingredientsByLetter = this.groupIngredientsByLetter(this.ingredients)
    },
    
    // ============================================================================
    // Filtering & Search
    // ============================================================================
    
    formatDescription(description) {
      if (!description) return ''
      // Product descriptions are HTML - return as-is (for drawer)
      return description
    },
    
    formatDescriptionTruncated(description) {
      try {
        if (!description) return ''
        
        // Strip HTML tags to get plain text for truncation
        const text = String(description).replace(/<[^>]*>/g, '').trim()
        if (!text) return ''
        
        const maxLength = 200
        
        // Truncate to ~200 characters at word boundary
        if (text.length > maxLength) {
          // Find the last space before maxLength
          const truncatedText = text.substring(0, maxLength)
          const lastSpaceIndex = truncatedText.lastIndexOf(' ')
          
          // If we found a space, cut there; otherwise cut at maxLength
          const cutIndex = lastSpaceIndex > 0 ? lastSpaceIndex : maxLength
          return text.substring(0, cutIndex).trim() + '...'
        }
        
        return text
      } catch (error) {
        console.error('Error formatting description:', error)
        // Fallback: strip HTML and return
        return String(description || '').replace(/<[^>]*>/g, '')
      }
    },
    
    toggleTypeFilter(typeTitle) {
      const index = this.selectedTypes.indexOf(typeTitle)
      
      if (index > -1) {
        this.selectedTypes.splice(index, 1)
        this.applyFilters()
        return
      }
      
      this.selectedTypes.push(typeTitle)
      this.applyFilters()
    },
    
    applyFilters() {
      if (this.selectedTypes.length === 0) {
        this.ingredientsByLetter = this.groupIngredientsByLetter(this.ingredients)
        return
      }
      
      const filteredIngredients = this.ingredients.filter(ingredient => 
        this.selectedTypes.includes(ingredient.typeName)
      )
      this.ingredientsByLetter = this.groupIngredientsByLetter(filteredIngredients)
      this.emptyLetterGroups = []
    },
    
    // ============================================================================
    // UI Navigation & Scrolling
    // ============================================================================
    
    loadMore() {
      const dataGroupsCount = this.ingredientsByLetter.length
      if (dataGroupsCount > this.visibleLetterGroupCount) {
        this.visibleLetterGroupCount += INITIAL_VISIBLE_GROUPS
      }
    },
    
    handleLetterClick(letter) {
      const hasData = this.ingredientsByLetter.some(g => g.letter === letter)
      
      // Letter has data - find its position and reveal up to it
      if (hasData) {
        const index = this.displayGroups.findIndex(g => g.letter === letter)
        const requiredCount = index + 1
        const newCount = Math.max(requiredCount, this.visibleLetterGroupCount)
        this.visibleLetterGroupCount = newCount
        
        this.$nextTick(() => {
          this.scrollToLetter(letter)
        })
        return
      }
      
      // Letter has no data - add to empty groups
      const wasAlreadyEmpty = this.emptyLetterGroups.includes(letter)
      if (!wasAlreadyEmpty) {
        this.emptyLetterGroups.push(letter)
      }
      
      // Wait for displayGroups to update with the new empty group
      this.$nextTick(() => {
        const index = this.displayGroups.findIndex(g => g.letter === letter)
        const requiredCount = index + 1
        
        // Calculate new visible count
        // If we just added a new empty group, we need to maintain visibility of all previously visible groups
        // The new group might have been inserted in the middle, pushing others down
        const newCount = (!wasAlreadyEmpty && index < this.visibleLetterGroupCount)
          ? this.visibleLetterGroupCount + 1
          : Math.max(requiredCount, this.visibleLetterGroupCount)
        
        this.visibleLetterGroupCount = newCount
        
        this.$nextTick(() => {
          this.scrollToLetter(letter)
        })
      })
    },
    
    scrollToLetter(letter) {
      const element = document.getElementById('letter-' + letter)
      if (!element) return
      
      // Remove any existing highlights
      document.querySelectorAll('.ig-body--letter.highlight-active').forEach(el => {
        el.classList.remove('highlight-active')
      })
      
      // Calculate scroll offset
      const rootStyles = getComputedStyle(document.documentElement)
      const announcementBarHeight = parseFloat(rootStyles.getPropertyValue('--announcement-bar-height')) || 0
      const headerHeight = parseFloat(rootStyles.getPropertyValue('--header-height')) || 0
      const totalOffset = announcementBarHeight + headerHeight + 20 // Extra 20px breathing room
      
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - totalOffset
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
      
      // Highlight animation
      element.classList.add('highlight-active')
      setTimeout(() => element.classList.remove('highlight-active'), 1500)
    },

    // ============================================================================
    // Drawer Management
    // ============================================================================
    openDrawer() {
      this.isDrawerOpen = true
      // Lock body scroll
      document.body.style.overflow = 'hidden'
    },

    closeDrawer() {
      this.isDrawerOpen = false
      document.body.style.overflow = ''
    },

    handleLetterChange(event) {
      const letter = event.target.value
      if (!letter) return
      
      this.handleLetterClick(letter)
      this.closeDrawer()
    },

    /**
     * Ensure ingredient image is loaded and cached
     * Product images are already resolved URLs - no action needed
     * @param {Ingredient} ingredient - The ingredient to load image for
     */
    async ensureIngredientImageLoaded(ingredient) {
      // Product images are direct URLs, already populated in organizeIngredientsData
      // This method kept for compatibility but does nothing
      return Promise.resolve()
    },

    /**
     * Ensure ingredient roles are loaded and cached
     * Roles are already resolved comma-separated strings - no action needed
     * @param {Ingredient} ingredient - The ingredient to load roles for
     */
    async ensureIngredientRolesLoaded(ingredient) {
      // Roles are already comma-separated strings in metafield, populated in organizeIngredientsData
      // This method kept for compatibility but does nothing
      return Promise.resolve()
    },

    /**
     * Open ingredient detail drawer and fetch image/roles if needed
     * @param {Ingredient} ingredient - The ingredient to display
     */
    async openIngredientDrawer(ingredient) {
      if (!ingredient) {
        console.warn('Cannot open drawer: no ingredient data provided')
        return
      }
      this.selectedIngredient = ingredient
      this.isIngredientDrawerOpen = true
      document.body.style.overflow = 'hidden'
      
      await this.ensureIngredientImageLoaded(ingredient)
      await this.ensureIngredientRolesLoaded(ingredient)
      
      // Accessibility: scroll and focus management
      this.$nextTick(() => {
        const drawerContent = this.$refs.ingredientDrawerContent
        if (drawerContent) {
          drawerContent.scrollTop = 0
        }
        
        const closeButton = document.querySelector('.ig-ingredient-drawer-close')
        if (closeButton) {
          closeButton.focus()
        }
      })
    },

    closeIngredientDrawer() {
      this.isIngredientDrawerOpen = false
      document.body.style.overflow = ''
      
      // Clear after transition completes (450ms)
      setTimeout(() => {
        this.selectedIngredient = null
      }, 450)
    },

    // ============================================================================
    // Helper Utilities
    // ============================================================================
    
    // isValidImageUrl() removed - all product images are valid URLs

    /**
     * Parse attribute roles - returns resolved role names or fallback
     * @param {Ingredient} ingredient - The ingredient object
     * @returns {string} Comma-separated role names or "Not Available"
     */
    parseAttributeRoles(ingredient) {
      // Roles are already resolved comma-separated strings from product metafield
      if (ingredient?.resolvedRoles) {
        return ingredient.resolvedRoles === '' ? 'Not Available' : ingredient.resolvedRoles
      }
      
      if (ingredient?.attributeRoles) {
        return ingredient.attributeRoles
      }
      
      return 'Not Available'
    },

    /**
     * Check if ingredient has any attributes to display
     * @param {Ingredient} ingredient - The ingredient to check
     * @returns {boolean} True if ingredient has displayable attributes
     */
    hasAttributes(ingredient) {
      if (!ingredient) return false
      return !!(
        ingredient.attributeInciName || 
        ingredient.attributeIngredientOrigins || 
        ingredient.attributeRoles || 
        ingredient.attributeCommonName || 
        ingredient.attributeEwgScore
      )
    },

    // resolveRoleGids() removed - roles are comma-separated strings, no resolution needed
    // resolveMediaImageToPNG() removed - product images are direct URLs, no resolution needed
  },
  async mounted() {
    try {
      this.isLoading = true
      this.fetchError = false
      await this.fetchIngredients()
      this.resolveIngredientTypes() // Uses hardcoded type map (Phase 2)
      this.isLoading = false
    } catch (error) {
      console.error('❌ Error in mounted hook:', error)
      this.fetchError = true
      this.isLoading = false
    }
  }
}).mount('#IngredientsGlossary')

/*

Metaobject type: ingredients

Fields:

  1.  Title (text/string) - ingredients.title - Display name
  2.  Type (Refernce to metaobject) - ingredients.type - Used as filter
  3.  Description (multi-line text/rich text) - ingredients.description
  4.  Featured Image (file/image) - ingredients.featured_image
  5.  [Preview] Title (text/string) - ingredients.preview_title
  6.  [Preview] Icon (file/image) - ingredients.preview_icon
  7.  [Preview] Description (multi-line text/rich text) - ingredients.preview_description
  8.  [Attribute] INCI Name (text/string) - ingredients.attribute_inci_name
  9.  [Attribute] Ingredient origins (text/string) - ingredients.attribute_ingredient_origins
  10. [Attribute] Common Name (text/string) - ingredients.attribute_common_name
  11. [Attribute] Roles (Refernce to metaobject) - ingredients.attribute_roles
  12. [Attribute] EWG score (number/numeric) - ingredients.attribute_ewg_score


Example: data.data.metaobjects.edges
[
  {
    "node": {
      "id": "gid://shopify/Metaobject/151337894025",
      "handle": "moustache-republic-ingredient",
      "fields": [
        {
          "key": "attribute_common_name",
          "value": "BZTs"
        },
        {
          "key": "attribute_ewg_score",
          "value": "1"
        },
        {
          "key": "attribute_inci_name",
          "value": "Benzotriazole"
        },
        {
          "key": "attribute_ingredient_origins",
          "value": "Hydrocarbons"
        },
        {
          "key": "attribute_roles",
          "value": "[\"gid://shopify/Metaobject/151340318857\",\"gid://shopify/Metaobject/151340286089\"]"
        },
        {
          "key": "featured_image",
          "value": "gid://shopify/MediaImage/28473081397385"
        },
        {
          "key": "preview_description",
          "value": "Benzotriazoles (BZTs) are petrochemical-derived ingredients which are used for many functions."
        },
        {
          "key": "preview_icon",
          "value": "gid://shopify/MediaImage/28473088376969"
        },
        {
          "key": "title",
          "value": "Moustache Republic Ingredient"
        },
        {
          "key": "type",
          "value": "gid://shopify/Metaobject/151334813833"
        }
      ]
    }
  }
]


Image object example: 
{ 
  id: "gid://shopify/ImageSource/28681348743305",
  url: "https://cdn.shopify.com/s/files/1/0714/2994/7529/files/2-_2-ethoxyethoxy_ethanol_1.svg.png?v=1759463333",
  altText: null,
  width: 1842,
  height: 1182,
  thumbhash:
  "ZauCA4ARqSKGBlVgZ/dEV3h4iHdweNc="
}

*/